import { useEffect, useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import {
  Snackbar,
  Button,
  Alert,
  AlertTitle,
  IconButton,
  Box,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

const CHECK_INTERVAL = 60 * 60 * 1000; // Check for updates every hour

export function PWAUpdatePrompt() {
  const { t } = useTranslation();
  const [showPrompt, setShowPrompt] = useState(false);
  const reloadTriggeredRef = useRef(false);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        // Periodically check for updates
        setInterval(() => {
          registration.update();
        }, CHECK_INTERVAL);
      }
    },
    onRegisterError(error) {
      console.error('SW registration error:', error);
    },
  });

  useEffect(() => {
    if (needRefresh) {
      if (!reloadTriggeredRef.current) {
        reloadTriggeredRef.current = true;
        void updateServiceWorker(true);
      }
      setShowPrompt(true);
    }
  }, [needRefresh, updateServiceWorker]);

  const handleUpdate = () => {
    updateServiceWorker(true);
    setShowPrompt(false);
  };

  const handleClose = () => {
    setShowPrompt(false);
    setNeedRefresh(false);
  };

  return (
    <Snackbar
      open={showPrompt}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ mb: { xs: 7, sm: 0 } }}
    >
      <Alert
        severity="info"
        variant="filled"
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleUpdate}
            >
              {t('pwa.update')}
            </Button>
            <Tooltip title={t('common.close')}>
              <IconButton
                size="small"
                color="inherit"
                onClick={handleClose}
                aria-label={t('common.close')}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        }
        sx={{ width: '100%', maxWidth: 450 }}
      >
        <AlertTitle>{t('pwa.updateAvailable')}</AlertTitle>
        {t('pwa.updateDescription')}
      </Alert>
    </Snackbar>
  );
}
